// "Add to your home screen" prompt for the web app / PWA (D-145). Goal: make the
// PWA feel like a real app the user installs, not a website — a clean card that
// installs in one tap where the platform allows it, and walks iOS users through
// the manual step Safari requires. No-op once installed, and snoozes on dismiss.
//
// Platform reality (why the two modes):
//   • Chromium (Android + desktop Chrome/Edge/Brave) fires `beforeinstallprompt`,
//     so we can offer a real one-tap Install button.
//   • iOS Safari has no such event — the only way onto the home screen is
//     Share → "Add to Home Screen" — so we show the instruction instead.
//   • Chrome/Firefox on iOS can't install at all, so we stay quiet there.

import { el } from "./dom.js";

const CARD_ID = "ta-install-card";
const SNOOZE_KEY = "ta-install-snooze";
const EXT_CARD_ID = "ta-ext-card";
const EXT_SNOOZE_KEY = "ta-ext-snooze";
const SNOOZE_MS = 14 * 24 * 3600 * 1000; // don't nag: 14 days after a dismiss

// Chrome Web Store URL for the extension. Placeholder until the listing is live;
// the desktop "get the extension" prompt stays hidden while it still says REPLACE.
const STORE_URL = "https://chromewebstore.google.com/detail/REPLACE_AT_LAUNCH";

function isMobile(): boolean {
  return (
    window.matchMedia?.("(pointer: coarse)").matches === true ||
    /android|iphone|ipad|ipod/i.test(navigator.userAgent) ||
    window.innerWidth < 700
  );
}

function isChromium(): boolean {
  // Chrome / Brave / Edge / Opera — where our extension can be installed.
  return /chrome|chromium|crios|edg|opr/i.test(navigator.userAgent) && !/firefox|fxios/i.test(navigator.userAgent);
}

// The saved beforeinstallprompt event (Chromium only), if one has fired.
let deferred: (Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> }) | null = null;
let wired = false;

function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIOS(): boolean {
  const ua = navigator.userAgent;
  // iPadOS 13+ reports as "MacIntel" but has a touch screen — catch that too.
  return /iphone|ipad|ipod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isIOSSafari(): boolean {
  const ua = navigator.userAgent;
  // Chrome (CriOS) / Firefox (FxiOS) on iOS can't add to home screen — Safari only.
  return isIOS() && /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
}

function snoozed(key: string): boolean {
  try {
    const t = Number(localStorage.getItem(key) ?? 0);
    return t > 0 && Date.now() - t < SNOOZE_MS;
  } catch {
    return false;
  }
}

function snooze(key: string): void {
  try {
    localStorage.setItem(key, String(Date.now()));
  } catch {
    /* private mode — fine, we just re-ask next time */
  }
}

function dismiss(): void {
  document.getElementById(CARD_ID)?.remove();
  snooze(SNOOZE_KEY);
}

/** Build the bottom card. `mode` decides the action area. */
function showCard(mode: "install" | "ios"): void {
  if (isStandalone() || snoozed(SNOOZE_KEY)) return;
  if (document.getElementById(CARD_ID)) return;

  const card = el("div", {
    attrs: { id: CARD_ID },
    style: {
      position: "fixed",
      left: "0",
      right: "0",
      bottom: "0",
      zIndex: "9999",
      margin: "0 auto",
      maxWidth: "440px",
      background: "var(--bg-elevated)",
      borderTop: "1px solid var(--border)",
      boxShadow: "0 -8px 30px rgba(0,0,0,0.4)",
      padding: "14px 16px calc(14px + env(safe-area-inset-bottom))",
      display: "flex",
      gap: "12px",
      alignItems: "center",
    },
  });

  const icon = el("img", {
    attrs: { src: "icons/app128.png", alt: "" },
    style: { width: "40px", height: "40px", borderRadius: "10px", flex: "none" },
  });

  const copy = el("div", { style: { flex: "1", minWidth: "0" } });
  copy.appendChild(el("div", { text: "Add TickAhead to your home screen", style: { font: "var(--font-header)", color: "var(--text-primary)" } }));
  copy.appendChild(
    el("div", {
      text:
        mode === "ios"
          ? "Tap the Share button, then “Add to Home Screen” — it opens like any app, full-screen."
          : "Install it once and it lives with your other apps — full-screen, with alerts.",
      style: { font: "var(--font-caption)", color: "var(--text-secondary)", marginTop: "2px" },
    })
  );

  const actions = el("div", { style: { display: "flex", flexDirection: "column", gap: "6px", flex: "none" } });
  if (mode === "install") {
    const installBtn = el("button", {
      className: "pill",
      text: "Install",
      style: { background: "var(--accent-blue)", color: "#fff", border: "0", whiteSpace: "nowrap" },
    });
    installBtn.addEventListener("click", () => {
      if (!deferred) return;
      void deferred.prompt();
      void deferred.userChoice.then(() => {
        deferred = null;
        dismiss();
      });
    });
    actions.appendChild(installBtn);
  }
  const close = el("button", {
    text: mode === "ios" ? "Got it" : "Not now",
    style: { background: "none", border: "0", color: "var(--text-secondary)", cursor: "pointer", font: "var(--font-caption)" },
    onClick: dismiss,
  });
  actions.appendChild(close);

  card.append(icon, copy, actions);
  document.body.appendChild(card);
}

/**
 * Mount the install prompt. Safe to call more than once (idempotent by card id),
 * so both the sign-in gate and the main app can call it after they render.
 */
export function mountInstallPrompt(): void {
  if (isStandalone()) return; // already installed — nothing to do

  if (!wired) {
    wired = true;
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault(); // keep Chrome's mini-infobar from firing; we drive it
      deferred = e as typeof deferred;
      // On mobile we push the "add to home screen" card; on desktop the richer
      // move is our browser extension, so we leave the PWA install to the
      // browser's own address-bar icon and show the extension card instead.
      if (isMobile()) showCard("install");
    });
    window.addEventListener("appinstalled", () => {
      deferred = null;
      document.getElementById(CARD_ID)?.remove();
    });
  }

  // iOS Safari never fires beforeinstallprompt, so show the manual instruction
  // directly. (Chromium waits for the event above, then shows the Install card.)
  if (isIOSSafari()) showCard("ios");
}

/**
 * Desktop-only: suggest installing the browser extension (the "power-up" whose
 * exclusive job is cross-tab / other-site integration and the side panel), the
 * way Grammarly nudges you to add its extension when you're on their web app.
 * Dormant until STORE_URL is filled in with the real listing (so it never links
 * to a dead store page pre-launch). Snoozes 14 days on dismiss.
 *
 * Note: this can't tell whether the extension is already installed — a web page
 * can't see other extensions without the extension injecting a marker (a small
 * content script). Left as a snooze-on-dismiss nudge for now; add the marker
 * later if the repeat-prompt for existing users becomes a real annoyance.
 */
export function mountExtensionSuggestion(): void {
  if (STORE_URL.includes("REPLACE")) return; // listing not live yet — stay hidden
  if (isMobile() || !isChromium()) return; // extension is a desktop-Chromium thing
  if (snoozed(EXT_SNOOZE_KEY)) return;
  if (document.getElementById(EXT_CARD_ID)) return;

  const card = el("div", {
    attrs: { id: EXT_CARD_ID },
    style: {
      position: "fixed",
      left: "0",
      right: "0",
      bottom: "0",
      zIndex: "9999",
      margin: "0 auto",
      maxWidth: "440px",
      background: "var(--bg-elevated)",
      borderTop: "1px solid var(--border)",
      boxShadow: "0 -8px 30px rgba(0,0,0,0.4)",
      padding: "14px 16px",
      display: "flex",
      gap: "12px",
      alignItems: "center",
    },
  });
  card.appendChild(el("img", { attrs: { src: "icons/app128.png", alt: "" }, style: { width: "40px", height: "40px", borderRadius: "10px", flex: "none" } }));
  const copy = el("div", { style: { flex: "1", minWidth: "0" } });
  copy.appendChild(el("div", { text: "Add the TickAhead browser extension", style: { font: "var(--font-header)", color: "var(--text-primary)" } }));
  copy.appendChild(el("div", { text: "Build a strategy right on any chart, from a side panel that follows you across tabs.", style: { font: "var(--font-caption)", color: "var(--text-secondary)", marginTop: "2px" } }));
  const actions = el("div", { style: { display: "flex", flexDirection: "column", gap: "6px", flex: "none" } });
  const add = el("a", {
    text: "Add to browser",
    attrs: { href: STORE_URL, target: "_blank", rel: "noopener" },
    className: "pill",
    style: { background: "var(--accent-blue)", color: "#fff", border: "0", whiteSpace: "nowrap", textAlign: "center", textDecoration: "none" },
  });
  add.addEventListener("click", () => snooze(EXT_SNOOZE_KEY)); // opened it — don't nag again
  actions.appendChild(add);
  actions.appendChild(
    el("button", {
      text: "Not now",
      style: { background: "none", border: "0", color: "var(--text-secondary)", cursor: "pointer", font: "var(--font-caption)" },
      onClick: () => {
        document.getElementById(EXT_CARD_ID)?.remove();
        snooze(EXT_SNOOZE_KEY);
      },
    })
  );
  card.append(copy, actions);
  document.body.appendChild(card);
}
